import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Film, Image as ImageIcon } from "lucide-react";
import { PageHero } from "@/pages/About";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { resolveMediaUrl } from "@/pages/ImpactHub";

function toEmbed(url) {
    if (!url) return null;
    const y = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
    if (y) return `https://www.youtube.com/embed/${y[1]}`;
    const v = url.match(/vimeo\.com\/(\d+)/);
    if (v) return `https://player.vimeo.com/video/${v[1]}`;
    return url;
}

function isNativeVideo(url) {
    return /\.(mp4|webm|ogg)(\?|$)/i.test(url || "");
}

export default function MediaCentre() {
    const [data, setData] = useState({ photos: [], videos: [], documents: [] });
    const [lightbox, setLightbox] = useState(null);
    const [q, setQ] = useState("");

    useEffect(() => { api.get("/public/media").then(({ data }) => setData(data)).catch(() => {}); }, []);

    const filter = (list) => q ? list.filter((x) => JSON.stringify(x).toLowerCase().includes(q.toLowerCase())) : list;

    return (
        <div data-testid="media-centre">
            <PageHero title="Media Centre" subtitle="Photos · Videos · Reports · Downloads" />
            <section className="gsf-section">
                <div className="gsf-container">
                    <input type="search" placeholder="Search across photos, videos and reports…" value={q} onChange={(e) => setQ(e.target.value)}
                        data-testid="media-search" className="w-full max-w-md rounded-full px-5 py-3 border border-gray-200 focus:outline-none focus:border-[color:var(--gsf-green-700)]" />

                    <Tabs defaultValue="photos" className="mt-8">
                        <TabsList className="grid grid-cols-3 max-w-md bg-[color:var(--gsf-soft-green)] rounded-full p-1">
                            <TabsTrigger value="photos" className="rounded-full data-[state=active]:bg-[color:var(--gsf-green-700)] data-[state=active]:text-white" data-testid="tab-photos">Photos ({data.photos.length})</TabsTrigger>
                            <TabsTrigger value="videos" className="rounded-full data-[state=active]:bg-[color:var(--gsf-green-700)] data-[state=active]:text-white" data-testid="tab-videos">Videos ({data.videos.length})</TabsTrigger>
                            <TabsTrigger value="docs" className="rounded-full data-[state=active]:bg-[color:var(--gsf-green-700)] data-[state=active]:text-white" data-testid="tab-docs">Downloads ({data.documents.length})</TabsTrigger>
                        </TabsList>

                        <TabsContent value="photos" className="mt-8">
                            {filter(data.photos).length === 0 ? <EmptyState label="photos" /> : (
                                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="photos-grid">
                                    {filter(data.photos).map((p, i) => (
                                        <button key={i} onClick={() => setLightbox(resolveMediaUrl(p.url))} className="aspect-square overflow-hidden rounded-xl group" data-testid={`photo-${i}`}>
                                            <img src={resolveMediaUrl(p.url)} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </TabsContent>
                        <TabsContent value="videos" className="mt-8">
                            {filter(data.videos).length === 0 ? <EmptyState label="videos" /> : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="videos-grid">
                                    {filter(data.videos).map((v, i) => (
                                        <div key={i} className="gsf-card overflow-hidden" data-testid={`video-item-${i}`}>
                                            <div className="aspect-video bg-black">
                                                {isNativeVideo(v.url) ? (
                                                    <video src={resolveMediaUrl(v.url)} controls preload="metadata" className="w-full h-full" data-testid={`native-video-${i}`} />
                                                ) : (
                                                    <iframe src={toEmbed(v.url)} title={v.title} className="w-full h-full" allowFullScreen loading="lazy" />
                                                )}
                                            </div>
                                            {v.title && <div className="p-4 text-sm font-medium">{v.title}</div>}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </TabsContent>
                        <TabsContent value="docs" className="mt-8">
                            {filter(data.documents).length === 0 ? <EmptyState label="documents" /> : (
                                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="docs-grid">
                                    {filter(data.documents).map((d, i) => (
                                        <li key={i}>
                                            <a href={resolveMediaUrl(d.url)} target="_blank" rel="noreferrer" className="gsf-card p-4 flex items-center gap-3 hover:border-[color:var(--gsf-green-600)]" data-testid={`doc-item-${i}`}>
                                                <FileText size={20} className="text-[color:var(--gsf-green-700)]" />
                                                <span className="text-sm font-medium">{d.name || d.url}</span>
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </TabsContent>
                    </Tabs>
                </div>
            </section>

            {lightbox && (
                <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6" onClick={() => setLightbox(null)} data-testid="media-lightbox">
                    <img src={lightbox} alt="Enlarged view" className="max-w-full max-h-full object-contain" />
                </div>
            )}
        </div>
    );
}

function EmptyState({ label }) {
    return (
        <div className="gsf-card p-8 text-sm text-gray-500 max-w-xl">
            No {label} to display yet. Content will appear here as projects are published in the admin dashboard.
        </div>
    );
}
